import { Box, Button, Divider, Paper, Typography } from '@mui/material';
import { useCallback } from 'react';
import { SettingsTabContentProps } from '../../../interfaces/Settings/SettingsTabContentProps';
import { sharedStyles } from '../../../utils/Style';
import { SettingsRow } from '../../../components/Settings/SettingsRow';
import { SettingsValueType } from '../../../interfaces/Settings/SettingsValueType';
import { getSettingsValue, getUpdatedSettings } from '../../../utils/Settings';
import { GenericSettingsValue } from '../../../interfaces/Settings/GenericSettingsValue';
import { SettingsType } from '../../../interfaces/Settings/SettingsType';

export const VisualSettings = (props: SettingsTabContentProps) => {
  const handleChange = useCallback(
    (key: string, newValue: GenericSettingsValue) => {
      const newSettings = getUpdatedSettings(
        SettingsType.FRONTEND,
        key,
        props.settings,
        newValue,
      );

      const newSettingKeys = newSettings.map((s) => s.key);
      if (!newSettingKeys.includes('storefront_theme_color')) {
        newSettings.push({
          type: SettingsType.FRONTEND,
          key: 'storefront_theme_color',
          genericValue: { stringValue: 'custom_color' },
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }
      if (!newSettingKeys.includes('storefront_mail_theme_color')) {
        newSettings.push({
          type: SettingsType.FRONTEND,
          key: 'storefront_mail_theme_color',
          genericValue: { stringValue: 'custom_color' },
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      }

      props.onChange(newSettings);
    },
    [props.settings],
  );

  const handleSave = () => {
    props.onSave([
      'storefront_theme_color',
      'storefront_mail_theme_color',
      'storefront_custom_theme_color',
      'storefront_custom_mail_theme_color',
      'storefront_ternary_color',
      'storefront_blogs_color',
      'storefront_hidden_title',
      'storefront_homepage_meta_description',
      'popular_products_text',
      'popular_categories_text',
      'related_products_text',
      'blog_text',
      'blog_description',
      'highlight_products',
      'todays_product',
    ]);
  };

  return (
    <Paper
      sx={{
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        height: '100%',
        boxSizing: 'border-box',
      }}
    >
      <Typography
        sx={{
          ...sharedStyles.h5,
          fontWeight: 'bold',
        }}
      >
        Görsel
      </Typography>
      <Divider />

      <SettingsRow
        name='Birincil Renk'
        valueType={SettingsValueType.COLOR}
        value={getSettingsValue(
          props.settings,
          'storefront_custom_theme_color',
        )}
        onChange={(newValue) =>
          handleChange('storefront_custom_theme_color', newValue)
        }
      />
      <SettingsRow
        name='İkincil Renk'
        valueType={SettingsValueType.COLOR}
        value={getSettingsValue(
          props.settings,
          'storefront_custom_mail_theme_color',
        )}
        onChange={(newValue) =>
          handleChange('storefront_custom_mail_theme_color', newValue)
        }
      />
      <SettingsRow
        name='Üçüncül Renk'
        valueType={SettingsValueType.COLOR}
        value={getSettingsValue(props.settings, 'storefront_ternary_color')}
        onChange={(newValue) =>
          handleChange('storefront_ternary_color', newValue)
        }
      />
      <SettingsRow
        name='Bloglar Bölümü Rengi'
        valueType={SettingsValueType.COLOR}
        value={getSettingsValue(props.settings, 'storefront_blogs_color')}
        onChange={(newValue) =>
          handleChange('storefront_blogs_color', newValue)
        }
      />
      <SettingsRow
        name='Arkaplan Sekme Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'storefront_hidden_title')}
        onChange={(newValue) =>
          handleChange('storefront_hidden_title', newValue)
        }
      />
      <SettingsRow
        name='Anasayfa Meta Açıklama'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(
          props.settings,
          'storefront_homepage_meta_description',
        )}
        onChange={(newValue) =>
          handleChange('storefront_homepage_meta_description', newValue)
        }
      />
      <SettingsRow
        name='Anasayfa Popüler Ürünler Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'popular_products_text')}
        onChange={(newValue) => handleChange('popular_products_text', newValue)}
      />
      <SettingsRow
        name='Anasayfa Popüler Kategoriler Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'popular_categories_text')}
        onChange={(newValue) =>
          handleChange('popular_categories_text', newValue)
        }
      />
      <SettingsRow
        name='Benzer Ürünler Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'related_products_text')}
        onChange={(newValue) => handleChange('related_products_text', newValue)}
      />
      <SettingsRow
        name='Anasayfa Blog Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'blog_text')}
        onChange={(newValue) => handleChange('blog_text', newValue)}
      />
      <SettingsRow
        name='Blog Alt Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'blog_description')}
        multiline
        placeholder='En yeni trendler, cilt bakımı sırları ve fazlası… Yeni blog yazılarımıza göz atın!'
        onChange={(newValue) => handleChange('blog_description', newValue)}
      />
      <SettingsRow
        name='Haftanın Ürünleri Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'highlight_products')}
        onChange={(newValue) => handleChange('highlight_products', newValue)}
      />
      <SettingsRow
        name='Bugünün Ürünleri Başlığı'
        localized
        valueType={SettingsValueType.TEXT}
        value={getSettingsValue(props.settings, 'todays_product')}
        onChange={(newValue) => handleChange('todays_product', newValue)}
      />
      <Box sx={{ display: 'flex', flexGrow: 1 }} />
      <Box display='flex' justifyContent='flex-end'>
        <Button
          variant='contained'
          onClick={handleSave}
          sx={{ ...sharedStyles.buttonText, mt: 1.5 }}
        >
          Kaydet
        </Button>
      </Box>
    </Paper>
  );
};
